import { type SyntheticEvent, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  ChevronDownIcon,
  CircleAlertIcon,
  CornerLeftUpIcon,
  PlusIcon,
} from "@lucide/icons";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import type { OpenAPI } from "simplydo/interfaces";
import { RadioCard } from "simplydo/react/components/RadioCard/RadioCard";
import { RichTextViewer } from "simplydo/react/richtext";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxWhenChecked,
  CheckboxContent,
  CheckboxHeader,
  CheckboxChildren,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import {
  Form,
  FormDropdown,
  FormField,
  FormTextArea,
} from "simplydo/react/ui/form";
import { H4, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { getImpactTypeIcon } from "components/lib/Ideas/ImpactStatements/impactIcons";

import type {
  DropdownFieldValue,
  IdeasLibUser,
  ImpactStatementWithCreator,
  MappedImpactCategory,
  OrganisationImpact,
  OrganisationImpactCategory,
} from "../types";
import { dropdownStringValue } from "../types";
import { formatOption, impactStageDefinitions } from "./statementUtils";
import { useImpactStatements } from "./useImpactStatements";

type ImpactStatementProps = {
  ideaId: string;
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  editable?: boolean;
  rangeInputType?: "auto" | "allowCustom";
  showPreviousStatement?: boolean;
};

const ImpactStatementView = ({
  ideaId,
  stage,
  editable,
  rangeInputType,
  showPreviousStatement,
}: ImpactStatementProps) => {
  const { t } = useTranslation();
  const {
    impactStatements,
    statementDefinition,
    creatingStage,
    createImpactStatement,
    patchImpactStatement,
    patchStatementImpact,
  } = useImpactStatements(ideaId);

  const impactStatement = impactStatements[stage] as
    | ImpactStatementWithCreator
    | undefined;
  const previousStage = impactStageDefinitions[stage]?.previousStage;
  const previousStatement = (
    showPreviousStatement && previousStage
      ? impactStatements[previousStage]
      : undefined
  ) as ImpactStatementWithCreator | undefined;
  const [impactList, setImpactList] = useState<OrganisationImpact[]>([]);
  const [impactCategories, setImpactCategories] = useState<
    OrganisationImpactCategory[]
  >([]);
  const user: IdeasLibUser = useAppSelector((state) => state.user);

  const [mappedImpactCategories, setMappedImpactCategories] = useState<
    MappedImpactCategory[]
  >([]);
  useEffect(() => {
    setMappedImpactCategories(
      [{ name: "Default category", order: -1 } as MappedImpactCategory]
        .concat(impactCategories)
        .map((category) => {
          const impacts = impactList
            .filter((i) => i.category?.id === category._id)
            .sort(
              (a, b) => (a.category?.order || 0) - (b.category?.order || 0),
            );
          return { ...category, impacts };
        })
        .sort((a, b) => a.order - b.order),
    );
  }, [impactCategories, impactList]);

  useEffect(() => {
    let isMounted = true;
    if (user?.ownerOrganisation?._id) {
      api.organisations.getImpacts(
        user.ownerOrganisation._id,
        ({ impacts, impactCategories }) => {
          if (!isMounted) return;
          setImpactList(impacts);
          setImpactCategories(impactCategories);
        },
        () => {},
      );
    }

    return () => {
      isMounted = false;
    };
  }, [user]);
  if (editable && !impactStatement) {
    return (
      <div key={stage}>
        {editable &&
          statementDefinition?.helpText &&
          stage in statementDefinition.helpText &&
          statementDefinition.helpText[stage] && (
            <RichTextViewer value={statementDefinition.helpText[stage]} />
          )}

        <Divider section />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Button
            loading={creatingStage === stage}
            onClick={() => {
              createImpactStatement({ impacts: {}, description: "" }, stage);
            }}
            color="secondary"
          >
            <Icon icon={PlusIcon} />
            Begin {t("generic.impact")} statement
          </Button>
        </div>
      </div>
    );
  }

  if (!statementDefinition) {
    return null;
  }

  return (
    <div key={stage}>
      {editable &&
        statementDefinition.helpText &&
        stage in statementDefinition.helpText &&
        statementDefinition.helpText[stage] && (
          <RichTextViewer value={statementDefinition.helpText[stage]} />
        )}
      <Divider hidden />
      <Accordion styled fluid>
        {mappedImpactCategories.map((group) => {
          const enabledImpacts = group?.impacts?.filter(
            (impact) =>
              statementDefinition?.impacts?.[impact._id]?.enabled &&
              !statementDefinition?.impacts?.[impact._id]?.hiddenFrom?.includes(
                stage,
              ),
          );
          if (!enabledImpacts || enabledImpacts.length === 0) {
            return null;
          }

          const chosenImpacts = enabledImpacts.filter(
            (impact) => impactStatement?.impacts?.[impact._id]?.enabled,
          );
          const hideUnselectedImpacts = !editable && stage === "userEstimate";
          const visibleImpacts = hideUnselectedImpacts
            ? chosenImpacts
            : enabledImpacts;

          if (!visibleImpacts || visibleImpacts.length === 0) {
            return null;
          }

          return (
            <>
              <AccordionTitle
                tabIndex={0}
                active={group.visible}
                onKeyDown={handleClickForKeyboard(() => {
                  setMappedImpactCategories((prev) =>
                    prev.map((g) =>
                      g._id === group._id ? { ...g, visible: !g.visible } : g,
                    ),
                  );
                })}
                onClick={() => {
                  setMappedImpactCategories((prev) =>
                    prev.map((g) =>
                      g._id === group._id ? { ...g, visible: !g.visible } : g,
                    ),
                  );
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {group.name}
                  </div>
                  <Label
                    size="small"
                    color={chosenImpacts.length > 0 ? "blue" : "grey"}
                  >
                    {hideUnselectedImpacts ? (
                      <>
                        {chosenImpacts?.length || 0}{" "}
                        {util.pluralise(
                          chosenImpacts?.length || 0,
                          `${t("generic.impact")}`,
                          `${t("generic.impacts")}`,
                          false,
                        )}{" "}
                        selected
                      </>
                    ) : (
                      <>
                        {chosenImpacts?.length || 0} /{" "}
                        {enabledImpacts?.length || 0}{" "}
                        {util.pluralise(
                          chosenImpacts?.length || 0,
                          `${t("generic.impact")}`,
                          `${t("generic.impacts")}`,
                          false,
                        )}{" "}
                        selected
                      </>
                    )}
                  </Label>
                </div>
              </AccordionTitle>
              <AccordionContent active={group.visible}>
                {visibleImpacts.map((impact, index) => {
                  return (
                    <>
                      {index !== 0 ? (
                        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                      ) : null}
                      <div key={impact._id}>
                        <Checkbox
                          toggle
                          disabled={!editable}
                          style={{ marginBottom: 0 }}
                          checked={
                            impactStatement?.impacts?.[impact._id]?.enabled
                          }
                          onChange={() => {
                            patchStatementImpact(stage, impact._id, (prev) => ({
                              ...prev,
                              enabled: !prev?.enabled,
                            }));
                          }}
                        >
                          <CheckboxHeader>
                            <div>
                              {impact.icon ? (
                                <Icon icon={getImpactTypeIcon(impact.type)} />
                              ) : null}
                              {impact.name}
                              {impact?.unit && <span> ({impact.unit})</span>}
                            </div>
                          </CheckboxHeader>
                          <CheckboxContent>
                            <span style={{ whiteSpace: "pre-line" }}>
                              {impact.description}
                            </span>
                          </CheckboxContent>
                          <CheckboxChildren>
                            {previousStatement ? (
                              <div>
                                <strong>Previous estimate:</strong> <br />
                                {previousStatement.impacts?.[impact._id]
                                  ?.enabled ? (
                                  <>
                                    <UserChip
                                      user={previousStatement.ownerCreatedBy}
                                      compact="very"
                                    />
                                    {previousStatement.impacts?.[impact._id]
                                      ?.choice?.value
                                      ? ` estimated "${previousStatement.impacts?.[impact._id]?.choice?.value}"`
                                      : null}
                                    {previousStatement.impacts?.[impact._id]
                                      ?.frequency
                                      ? ` ${previousStatement.impacts?.[impact._id]?.frequency}`
                                      : null}
                                    .
                                    <br />
                                    {previousStatement.impacts?.[impact._id]
                                      ?.justification
                                      ? `Their comment: "${previousStatement.impacts?.[impact._id]?.justification}"`
                                      : null}
                                  </>
                                ) : (
                                  <>
                                    <UserChip
                                      user={previousStatement.ownerCreatedBy}
                                      compact="very"
                                    />
                                    {` did not indicate this ${t("generic.impact")}.`}
                                  </>
                                )}
                              </div>
                            ) : null}
                          </CheckboxChildren>
                          <CheckboxWhenChecked>
                            <div style={{ marginTop: 15, marginBottom: 12 }}>
                              {statementDefinition.impacts?.[impact._id]
                                ?.options ||
                              statementDefinition.impacts?.[impact._id]
                                ?.customOptionEnabled ? (
                                <RadioCard.Group
                                  editable={editable}
                                  defaultValue={
                                    impactStatement?.impacts?.[impact._id]
                                      ?.choice?.custom
                                      ? "__custom"
                                      : (impactStatement?.impacts?.[impact._id]
                                          ?.choice?.id ?? "")
                                  }
                                  onValueChange={(value) => {
                                    patchStatementImpact(
                                      stage,
                                      impact._id,
                                      (prev) => ({
                                        ...prev,
                                        choice:
                                          value === "__custom"
                                            ? { custom: true }
                                            : { id: value },
                                      }),
                                    );
                                  }}
                                >
                                  {statementDefinition.impacts?.[
                                    impact._id
                                  ]?.options?.map((option) => {
                                    const optionValue = formatOption(
                                      impact,
                                      option,
                                    );
                                    return (
                                      <RadioCard.Item
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {optionValue}
                                        {impact?.unit && (
                                          <span> ({impact.unit})</span>
                                        )}
                                        {stage !== "userEstimate" &&
                                        option.weight !== undefined ? (
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              fontSize: "0.85em",
                                            }}
                                          >
                                            <Icon
                                              style={{ marginRight: 2 }}
                                              icon={CornerLeftUpIcon}
                                              size="small"
                                            />
                                            <div>
                                              Weight: {option.weight || 0}
                                            </div>
                                          </div>
                                        ) : null}
                                      </RadioCard.Item>
                                    );
                                  })}
                                  {statementDefinition.impacts?.[impact._id]
                                    ?.customOptionEnabled ||
                                  rangeInputType === "allowCustom" ? (
                                    <RadioCard.Item value="__custom">
                                      Other
                                    </RadioCard.Item>
                                  ) : null}
                                </RadioCard.Group>
                              ) : null}
                              <Form style={{ marginTop: 15 }}>
                                {impactStatement?.impacts?.[impact._id]?.choice
                                  ?.custom ? (
                                  editable ? (
                                    <FormField
                                      control={Input}
                                      editable={editable}
                                      label={`Define value${impact?.unit ? ` (${impact.unit})` : ""}`}
                                      type={
                                        impact.type === "numerical" ||
                                        impact.type === "money" ||
                                        impact.type === "time"
                                          ? "number"
                                          : "text"
                                      }
                                      required
                                      placeholder="Please provide a custom value"
                                      value={
                                        impactStatement?.impacts?.[impact._id]
                                          ?.choice?.value
                                      }
                                      onChange={(
                                        _e: SyntheticEvent,
                                        data: { value?: string | number },
                                      ) => {
                                        const nextValue = data.value ?? "";
                                        patchStatementImpact(
                                          stage,
                                          impact._id,
                                          (prev) => ({
                                            ...prev,
                                            choice: {
                                              custom: true,
                                              value:
                                                impact.type === "numerical" ||
                                                impact.type === "money" ||
                                                impact.type === "time"
                                                  ? parseFloat(
                                                      String(nextValue),
                                                    )
                                                  : nextValue,
                                            },
                                          }),
                                        );
                                      }}
                                    />
                                  ) : (
                                    <>
                                      <H5>Impact value</H5>
                                      <p>
                                        {
                                          impactStatement?.impacts?.[impact._id]
                                            ?.choice?.value
                                        }
                                      </p>
                                    </>
                                  )
                                ) : null}
                                {statementDefinition.impacts?.[impact._id]
                                  ?.frequencyEnabled ? (
                                  editable ? (
                                    <FormDropdown
                                      variant="outline"
                                      label={`${t("common:capitalise", { key: "generic.impact" })} frequency`}
                                      value={
                                        impactStatement?.impacts?.[impact._id]
                                          ?.frequency ?? "one-off"
                                      }
                                      onChange={(
                                        _e: SyntheticEvent,
                                        data: { value?: DropdownFieldValue },
                                      ) => {
                                        patchStatementImpact(
                                          stage,
                                          impact._id,
                                          (prev) => ({
                                            ...prev,
                                            frequency: dropdownStringValue(
                                              data.value,
                                            ) as
                                              | "weekly"
                                              | "one-off"
                                              | "monthly"
                                              | "annually"
                                              | undefined,
                                          }),
                                        );
                                      }}
                                      options={[
                                        {
                                          key: "one-off",
                                          value: "one-off",
                                          text: "One-off",
                                        },
                                        {
                                          key: "weekly",
                                          value: "weekly",
                                          text: "Weekly",
                                        },
                                        {
                                          key: "monthly",
                                          value: "monthly",
                                          text: "Monthly",
                                        },
                                        {
                                          key: "annually",
                                          value: "annually",
                                          text: "Annually",
                                        },
                                      ]}
                                    />
                                  ) : (
                                    <>
                                      <H5>
                                        {t("common:capitalise", {
                                          key: "generic.impact",
                                        })}{" "}
                                        frequency
                                      </H5>
                                      <p>
                                        {
                                          impactStatement?.impacts?.[impact._id]
                                            ?.frequency
                                        }
                                      </p>
                                    </>
                                  )
                                ) : null}
                                {/* Show warning if
                              - no choice made OR custom choice selected but no value provided
                              AND impact has valid options available
                              */}
                                {(!impactStatement?.impacts?.[impact._id]
                                  ?.choice ||
                                  (impactStatement?.impacts?.[impact._id]
                                    ?.choice?.custom === true &&
                                    !impactStatement?.impacts?.[impact._id]
                                      ?.choice?.value)) &&
                                  statementDefinition.impacts?.[impact._id]
                                    ?.options && (
                                    <Message color="yellow" size="small">
                                      <Icon icon={CircleAlertIcon} />
                                      <MessageHeader>
                                        No value provided
                                      </MessageHeader>
                                      <MessageContent>
                                        Please select or fill in a value for
                                        this {t("generic.impact")} from the
                                        options above.
                                      </MessageContent>
                                    </Message>
                                  )}
                                {editable ? (
                                  <>
                                    <Message color="blue" size="small">
                                      <Icon icon={CircleAlertIcon} />
                                      <MessageHeader>
                                        Justification notice
                                      </MessageHeader>
                                      <MessageContent>
                                        Please provide additional context for
                                        your selection. To ensure proper
                                        assessment it is important to provide an
                                        explanation of how this{" "}
                                        {t("generic.impact")} can be measured
                                        before and after its implementation.
                                      </MessageContent>
                                    </Message>
                                    <FormTextArea
                                      label="Details and justification"
                                      placeholder={`Why did you choose this ${t("generic.impact")}? How does it connect to your ${t("generic.idea")}? How can the ${t("generic.impact")} of your proposal be measured and evaluated?`}
                                      required={
                                        statementDefinition.requireJustification
                                      }
                                      defaultValue={
                                        impactStatement?.impacts?.[impact._id]
                                          ?.justification
                                      }
                                      onChange={(
                                        _e: SyntheticEvent,
                                        data: { value?: DropdownFieldValue },
                                      ) => {
                                        patchStatementImpact(
                                          stage,
                                          impact._id,
                                          (prev) => ({
                                            ...prev,
                                            justification: String(
                                              data.value ?? "",
                                            ),
                                          }),
                                        );
                                      }}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <H5>Details and justification</H5>
                                    <p>
                                      {
                                        impactStatement?.impacts?.[impact._id]
                                          ?.justification
                                      }
                                    </p>
                                  </>
                                )}
                              </Form>
                            </div>
                          </CheckboxWhenChecked>
                        </Checkbox>
                      </div>
                    </>
                  );
                })}
              </AccordionContent>
            </>
          );
        })}
      </Accordion>
      <Divider hidden />
      {editable ? (
        <Form>
          <H4>Additional comments</H4>
          <FormTextArea
            placeholder={`Provide any additional context, assumptions, or considerations relating to your estimations in this ${t("generic.impact")} statement...`}
            defaultValue={impactStatement?.description}
            onChange={(
              _e: SyntheticEvent,
              data: { value?: DropdownFieldValue },
            ) => {
              patchImpactStatement(stage, (prev) => ({
                ...prev,
                description: String(data.value ?? ""),
              }));
            }}
          />
        </Form>
      ) : impactStatement?.description ? (
        <>
          <H5>Additional comments</H5>
          <p>{impactStatement?.description}</p>
        </>
      ) : null}
    </div>
  );
};

export default ImpactStatementView;
